import { useCallback, useEffect, useRef, useState } from "react";
import { API_PREFIX, CHAT_MESSAGE_MAX_LENGTH, type ChatMessageDTO, type ChatServerMessage } from "@jaiho/shared";
import { wsUrl } from "../api/client";
import { requestChatWsTicket } from "../api/endpoints";

export type ChatConnectionState = "connecting" | "open" | "reconnecting" | "closed";

const RECONNECT_BASE_DELAY_MS = 1000;
const RECONNECT_MAX_DELAY_MS = 15000;

export function useChatSocket(enabled: boolean) {
  const [connectionState, setConnectionState] = useState<ChatConnectionState>("connecting");
  const [messages, setMessages] = useState<ChatMessageDTO[]>([]);
  const [presenceCount, setPresenceCount] = useState(0);

  const socketRef = useRef<WebSocket | null>(null);
  const reconnectAttempt = useRef(0);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closedByClient = useRef(false);
  // Holds the latest `connect` so reconnect scheduling never has to
  // reference `connect` from inside its own `useCallback` body (which the
  // hooks linter correctly flags as an immutability hazard).
  const connectRef = useRef<() => void>(() => {});

  const scheduleReconnect = useCallback(() => {
    setConnectionState("reconnecting");
    const delay = Math.min(RECONNECT_BASE_DELAY_MS * 2 ** reconnectAttempt.current, RECONNECT_MAX_DELAY_MS);
    reconnectAttempt.current += 1;
    reconnectTimer.current = setTimeout(() => connectRef.current(), delay);
  }, []);

  const connect = useCallback(async () => {
    // A fresh, short-lived ticket is required for every connection attempt
    // — including reconnects — since tickets are single-use and expire in
    // ~60s. The normal session bearer token never appears in the WS URL.
    let ticket: string;
    try {
      const response = await requestChatWsTicket();
      ticket = response.ticket;
    } catch {
      if (!closedByClient.current) scheduleReconnect();
      return;
    }

    if (closedByClient.current) return;

    const socket = new WebSocket(wsUrl(`${API_PREFIX}/chat/dev/ws`, { ticket }));
    socketRef.current = socket;
    setConnectionState((prev) => (prev === "connecting" ? "connecting" : "reconnecting"));

    socket.onopen = () => {
      reconnectAttempt.current = 0;
      setConnectionState("open");
    };

    socket.onmessage = (event) => {
      let parsed: ChatServerMessage;
      try {
        parsed = JSON.parse(event.data);
      } catch {
        return;
      }

      if (parsed.type === "history") {
        setMessages(parsed.messages);
      } else if (parsed.type === "message") {
        setMessages((prev) => [...prev.slice(-199), parsed.message]);
      } else if (parsed.type === "presence") {
        setPresenceCount(parsed.count);
      }
    };

    socket.onclose = () => {
      if (closedByClient.current) {
        setConnectionState("closed");
        return;
      }
      scheduleReconnect();
    };

    socket.onerror = () => {
      socket.close();
    };
  }, [scheduleReconnect]);

  useEffect(() => {
    connectRef.current = () => void connect();
  }, [connect]);

  useEffect(() => {
    if (!enabled) return;
    closedByClient.current = false;
    void connect();

    return () => {
      closedByClient.current = true;
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      socketRef.current?.close();
    };
  }, [enabled, connect]);

  const sendMessage = useCallback((body: string) => {
    const trimmed = body.trim().slice(0, CHAT_MESSAGE_MAX_LENGTH);
    if (!trimmed) return;
    socketRef.current?.send(JSON.stringify({ type: "send", body: trimmed }));
  }, []);

  return { connectionState, messages, presenceCount, sendMessage };
}
